import { AppConfigSource } from '../src/common/config/app-config';

export const environment: AppConfigSource = {
  app: {
    name: 'Sequal',
    tagline: 'Build your own workouts & flows.',
    animatedSplash: true,
    environmentName: 'Development',
    production: false,
    domain: 'sequal.app.development',
    comingSoon: false,
    // Enables the org/individual sign-up chooser (ADR-0026/0100). MUST match the
    // API's ORGANIZATIONS_ENABLED — the server is authoritative.
    organizationsEnabled: true,
    appId: 'app.sequal.development',
    port: 3030,
    // First-visit default only; a user-chosen mode (persisted) always wins.
    defaultThemeMode: 'dark',
  },
  captcha: {
    isCaptchaEnabled: true,
    turnstileSiteKey: '0x4AAAAAADNqDhayer0baz0R',
  },
  backend: {
    apiWebEndpoint: 'https://api-development.sequal.app',
    apiAndroidEndpoint: 'https://api-development.sequal.app',
    apiIosEndpoint: 'https://api-development.sequal.app',
  },
  registration: {
    publicKey: `-----BEGIN PUBLIC KEY-----
MIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEA3JxRgwfzq9P425+OKV9a
UjzTvGd+Gaixug2F4WLlw+Nbfav9gmPrakdh77lO9BHljjNnFhnHzj5kCyO+OUCp
YKAEUy5Bv9NF/9w+b2TIYJdGlkLm3mxLctV456hw6ty1Gd8VhbgMWJ3/aBTFX7eD
1vakLMmdCfo+F7IDQaMo/KVOhcmZgijtoICp2kguSW1QnOjc0e83KnTgO4/6D6AS
AiH9FeKYKKUqRxqUDpVEftEv0iUutpMPMcvgWr4n2CT6y8DH+Tc8W8qnfVrzX3Hx
+t2GtXPSnkOqqILokXKu1YJxgesCGRZyLlyYbj0NDaQMbzWEmCHesIP2oIACoUAV
Pm1l2F7S4xz8DkWjd9duuiRXD4Gvw1eSCY589V8dAfuPUEkEov3QDZP8ohxTjxdx
slUbPPa72f7IQbR4FhzFJZ3wFNSh5C8yndVJgpzO7K8AGQJkPLFOfzTS6CA30tnY
BdtPcXVxMFt/xp7bSsDYjJ5NelIw7ZOs4V0cidL6obbfOz95XNx6IUSG0oHwStCX
oov2pnbYk+m+EXZgPtwGV/HbktlFEICpwX3UwvGavlXoZ2xBFWi1Hei6qvBQJXpU
VQn2tQa3CrmM4Lm+saGAY7hO4gJtz97vsVGErxBhSXUo8cvwQ0F5zM7ocPp3RCwR
5UeWVuYvkcKPCD1K3+tfnSECAwEAAQ==
-----END PUBLIC KEY-----`,
  },
  export: {
    keyId: 'export-v1',
    verifyPublicKey: `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEq2zEOJpQFyyBB73Fv6HLECxt0ad/
TxMIxkxTYzvH70lxGPiYH84Avjtkn83932WTtjcFRQiYXfI5xuAdjF0D3w==
-----END PUBLIC KEY-----`,
  },
  table: {
    pageSize: 30,
  },
  externalProvider: {
    google: {
      authorizationBaseUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      jsScriptUrl: 'https://accounts.google.com/gsi/client',
      web: {
        clientId:
          '937157844674-v5n3l7pbn3751p832ucqdcmc17lqicvp.apps.googleusercontent.com',
        redirectUrl: 'https://app-development.sequal.app/oauth2redirect/google',
      },
      android: {
        clientId:
          '937157844674-busbc9766su0vlsbdi2dacev9esc37kp.apps.googleusercontent.com',
        redirectUrl: 'app.sequal.development:/oauth2redirect/google',
      },
      ios: {
        clientId:
          '937157844674-6bdtbc4jb7ar7ttuljac28r2tlt7dlup.apps.googleusercontent.com',
        redirectUrl: 'app.sequal.development:/oauth2redirect/google',
      },
    },
    apple: {
      authorizationBaseUrl: 'https://appleid.apple.com/auth/authorize',
      jsScriptUrl:
        'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
      web: {
        clientId: 'app.sequal.signin',
        redirectUrl: 'https://app-development.sequal.app/oauth2redirect/apple',
      },
      bridge: {
        clientId: 'app.sequal.signin',
        redirectUrl:
          'https://api-development.sequal.app/authentication/oauth2redirect/apple-bridge',
        currentWebUrl: 'https://app-development.sequal.app',
        appRedirectUri: 'app.sequal.development:/oauth2redirect/apple',
      },
    },
  },
  messaging: {
    webSocketEndpoint: '/messaging',
  },
  links: {
    apiUrl: 'https://api-development.sequal.app',
    appUrl: 'https://app-development.sequal.app',
    adminUrl: 'https://admin-development.sequal.app',
    landingUrl: 'https://development.sequal.app',
  },
  media: {
    supportedTypes:
      'video/mp4,video/quicktime,video/webm,audio/wav,audio/mpeg,audio/mp4,audio/webm,audio/aac,audio/x-m4a,image/jpeg,image/png,image/webp',
    enableClientCompression: true,
    maxVideoDurationSec: 300,
    maxVideoBytes: 104857600,
    videoTargetHeight: 540,
    maxAudioDurationSec: 7200, // 120 min global ceiling; per-context caps (activity 10 / group 60 / sequence 120) enforced in the picker
    maxAudioBytes: 52428800,
    audioBitrateKbps: 128,
    maxImageBytes: 1048576,
    imageMaxDimension: 1920,
    imageQuality: 0.8,
    thumbnailMaxDimension: 320,
    loudnessMeasurementEnabled: true,
    loudnessTargetLufs: -16,
  },
  // Workout-player mix levels (ADR-0079). Base volumes 1.0; ducking levels kept
  // at their tuned defaults. Every value is [0,1] (fadeMs in ms) and clamped by
  // AppConfigService; omit any key to fall back to its default.
  audio: {
    mediaVolume: 1.0, // activity audio / video
    musicVolume: 1.0, // group / sequence background music (primary channel)
    voiceVolume: 1.0, // spoken cues (TTS)
    metronomeVolume: 1.0, // cadence click when the activity sets no volume
    musicDuckedVolume: 0.15, // music while activity media or a cue plays over it
    mediaDuckedVolume: 0.15, // activity media while a cue speaks
    underlayVolumeFactor: 1.0, // continued sequence underlay — full level, same as the group
    fadeMs: 400, // duck-in/out + layer-switch ramp
  },
  // How long a paused workout keeps the phone's foreground service (Android) and
  // audio session (iPhone) before letting them go (ADR-0284). The workout keeps
  // its place; the next play brings them back.
  player: {
    idleReleaseMinutes: 15,
  },
};
